import { markierKlasse, markierTage, MARKIER_STUFE_LABELS, tagKurz } from '@netcon-work/shared'
import { Check, Sun } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn'
import {
  heuteSchluessel,
  useMarkieren,
  useMarkierungen,
  useOffeneMarkierungen,
} from '../../lib/markierung'
import { useAufgabenAllerWelten } from '../../lib/welt'
import { AufgabenVerweis } from '../AufgabeKontext'
import { SonnenMarkeFuer } from '../Markierung'
import type { Naechste } from './lage'

/**
 * **Markieren auf dem Telefon** ([Z-20](../../../../../docs/phasen/zusatz-20-mein-tag-markieren.md)).
 *
 * Nils, 05.10.2026: *„… oder über eine Markierungsfunktion auf mobilen Geräten."* Ziehen gibt es
 * hier nicht – der Finger soll scrollen. Stattdessen zwei Wege, beide **ohne langes Drücken**
 * (das gibt es in Work nirgends, und eine Geste, die man nicht sieht, findet niemand):
 *
 * | Weg | Wofür |
 * |---|---|
 * | ☀ an jeder Zeile → Blatt | eine Aufgabe, ein Daumen, ein Tipp |
 * | „Auswählen" → Haken → Leiste unten | der Abend: *„das, das und das nehme ich mir für morgen vor"* |
 *
 * Darüber der **Tagstreifen**: wie voll die nächsten Tage schon sind. Antippen zeigt, was für
 * diesen Tag markiert ist.
 */
export function TelefonVorlauf({
  rangfolge,
  children,
}: {
  rangfolge: readonly Naechste[]
  /** Der gewöhnliche Vorlauf – er steht da, solange weder ausgewählt noch gefiltert wird. */
  children: ReactNode
}) {
  const [auswahlModus, setAuswahlModus] = useState(false)
  const [gewaehlt, setGewaehlt] = useState<ReadonlySet<string>>(new Set())
  const [tagFilter, setTagFilter] = useState<string | null>(null)
  const markieren = useMarkieren()
  const heute = heuteSchluessel()

  const beenden = () => {
    setAuswahlModus(false)
    setGewaehlt(new Set())
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <TagStreifen
          gewaehlt={tagFilter}
          waehlen={(t) => {
            beenden()
            setTagFilter(tagFilter === t ? null : t)
          }}
        />
        <button
          type="button"
          onClick={() => (auswahlModus ? beenden() : (setTagFilter(null), setAuswahlModus(true)))}
          aria-pressed={auswahlModus}
          className={cn(
            'tap ml-auto shrink-0 cursor-pointer rounded-lg border px-3 py-1.5 text-[13px] font-semibold',
            auswahlModus
              ? 'border-accent bg-accent-soft text-accent-deep'
              : 'border-line2 bg-surface text-ink2',
          )}
        >
          {auswahlModus ? 'Fertig' : 'Auswählen'}
        </button>
      </div>

      {auswahlModus ? (
        <ul className="overflow-hidden rounded-card border border-line bg-surface">
          {rangfolge.map((a) => {
            const an = gewaehlt.has(a.id)
            return (
              <li key={a.id} className="border-b border-line last:border-0">
                <button
                  type="button"
                  aria-pressed={an}
                  onClick={() =>
                    /** Aus dem vorigen Stand, nicht aus dem gezeichneten – zwei schnelle Tipps zählen beide. */
                    setGewaehlt((vorher) => {
                      const neu = new Set(vorher)
                      if (neu.has(a.id)) neu.delete(a.id)
                      else neu.add(a.id)
                      return neu
                    })
                  }
                  className="tap flex w-full cursor-pointer items-center gap-3 px-3.5 py-3 text-left"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid size-[22px] shrink-0 place-items-center rounded-full border-2',
                      an ? 'border-sonne bg-sonne text-white' : 'border-line2 bg-surface',
                    )}
                  >
                    {an && <Check className="size-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] leading-snug">{a.title}</span>
                    {a.markiertAm && (
                      <span className="mt-1 block">
                        <SonnenMarkeFuer am={a.markiertAm} klein />
                      </span>
                    )}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : tagFilter ? (
        <AmTag tag={tagFilter} />
      ) : (
        children
      )}

      {/**
       * **Die Leiste unten** – über den Reitern, an der Stelle des ⚡-Knopfs, solange ausgewählt
       * wird. Drei große Knöpfe, unter jedem der echte Tag: freitags ist „Morgen" der Montag.
       */}
      {auswahlModus && (
        <div className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-40 border-t border-sonne-line bg-sonne-bg px-3 pt-2.5 pb-3">
          <div className="mb-2 flex items-center justify-between text-[13px] font-semibold text-sonne">
            <span>{gewaehlt.size} ausgewählt</span>
            {gewaehlt.size > 0 && (
              <button
                type="button"
                onClick={() => {
                  markieren.mutate({ taskIds: [...gewaehlt], am: null })
                  beenden()
                }}
                className="cursor-pointer text-[12px] font-normal text-ink2 underline"
              >
                Markierung entfernen
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {markierTage(heute).map(({ stufe, tag }) => (
              <button
                key={stufe}
                type="button"
                disabled={gewaehlt.size === 0}
                onClick={() => {
                  markieren.mutate({ taskIds: [...gewaehlt], am: tag })
                  beenden()
                }}
                className="tap cursor-pointer rounded-[10px] border border-sonne-line bg-surface px-1 py-2 text-[14px] leading-tight font-semibold text-sonne disabled:opacity-50"
              >
                {MARKIER_STUFE_LABELS[stufe]}
                <span className="block text-[11px] font-normal text-ink2">{tagKurz(tag)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** Heute 3 · Morgen 2 · Übermorgen 1 – je Tag die Zahl, Antippen filtert. */
function TagStreifen({
  gewaehlt,
  waehlen,
}: {
  gewaehlt: string | null
  waehlen: (tag: string) => void
}) {
  const markierungen = useOffeneMarkierungen()
  const heute = heuteSchluessel()
  return (
    <div className="flex min-w-0 gap-1.5 overflow-x-auto" role="group" aria-label="Markiert für">
      {markierTage(heute).map(({ stufe, tag }) => {
        const anzahl = [...markierungen.values()].filter(
          (am) => markierKlasse(am, heute) === stufe,
        ).length
        const an = gewaehlt === tag
        return (
          <button
            key={stufe}
            type="button"
            aria-pressed={an}
            onClick={() => waehlen(tag)}
            className={cn(
              'tap shrink-0 cursor-pointer rounded-full border px-2.5 py-1 text-[12px] whitespace-nowrap',
              an ? 'border-sonne bg-sonne-bg text-sonne' : 'border-line bg-surface text-ink2',
            )}
          >
            {stufe === 'heute' && (
              <Sun size={11} className="mr-1 inline align-[-1px] text-sonne" aria-hidden />
            )}
            {stufe === 'uebermorgen' ? 'Überm.' : MARKIER_STUFE_LABELS[stufe]}{' '}
            <b className="tabular text-sonne">{anzahl}</b>
          </button>
        )
      })}
    </div>
  )
}

/** Was für einen Tag markiert ist – ein Tipp öffnet die Aufgabe, ☀ verschiebt sie. */
function AmTag({ tag }: { tag: string }) {
  const markierungen = useOffeneMarkierungen()
  const { aufgaben } = useAufgabenAllerWelten()
  const heute = heuteSchluessel()
  const zeilen = aufgaben.filter((a) => {
    const am = markierungen.get(a.id)
    return am !== undefined && (am < heute ? heute : am) === tag
  })
  if (zeilen.length === 0) {
    return (
      <p className="rounded-card border border-dashed border-line2 px-4 py-3 text-[13px] text-muted">
        Für {tagKurz(tag)} ist nichts markiert. „Auswählen" oder ☀ an einer Zeile im Vorlauf.
      </p>
    )
  }
  return (
    <ul className="overflow-hidden rounded-card border border-sonne-line bg-surface">
      {zeilen.map((a) => (
        <li
          key={a.id}
          className="flex items-center gap-2 border-b border-line px-3.5 py-2.5 last:border-0"
        >
          <AufgabenVerweis
            aufgabeId={a.id}
            className="min-w-0 flex-1 text-left text-[15px] leading-snug"
          >
            {a.title}
          </AufgabenVerweis>
          <SonnenKnopf taskId={a.id} titel={a.title} />
        </li>
      ))}
    </ul>
  )
}

/**
 * **☀ an einer Zeile – und das Blatt dahinter.** Ein Tipp öffnet unten ein Blatt mit Heute ·
 * Morgen · Übermorgen und, wenn markiert, „Entfernen". Kein Popover am Finger: auf dem Telefon
 * liegt die Hand unten, und ein Blatt verdeckt nicht die Zeile, um die es geht.
 */
export function SonnenKnopf({ taskId, titel }: { taskId: string; titel: string }) {
  const [offen, setOffen] = useState(false)
  const markierungen = useMarkierungen()
  const markieren = useMarkieren()
  const heute = heuteSchluessel()
  const am = markierungen.get(taskId)
  const klasse = am ? markierKlasse(am, heute) : null

  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        aria-label={`${titel} für Mein Tag markieren`}
        className={cn(
          'tap grid size-10 shrink-0 cursor-pointer place-items-center rounded-[10px] border',
          am ? 'border-sonne-line bg-sonne-bg text-sonne' : 'border-line bg-surface text-muted',
        )}
      >
        <Sun className="size-[18px]" aria-hidden />
      </button>
      {/**
       * **Per Portal ans Ende der Seite** (05.10.2026, im Browser gefunden). Im Baum der Zeile
       * erbte das Blatt deren Transparenz und Ebene: es stand halb durchsichtig da, und der
       * ⚡-Knopf und die Reiterleiste lagen darüber.
       */}
      {offen &&
        createPortal(
          <div
            className="unter-leiste fixed inset-x-0 bottom-0 z-50 flex items-end bg-ink/35"
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) setOffen(false)
            }}
          >
            <div
              role="dialog"
              aria-label={`Mein Tag für ${titel}`}
              className="grid w-full gap-2 rounded-t-[18px] bg-surface px-3.5 pt-3.5 pb-[calc(18px+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgb(0_0_0/0.15)]"
            >
              <p className="truncate text-[13px] font-medium text-ink2">{titel}</p>
              {markierTage(heute).map(({ stufe, tag }) => {
                const aktiv = klasse === stufe
                return (
                  <button
                    key={stufe}
                    type="button"
                    aria-pressed={aktiv}
                    onClick={() => {
                      markieren.mutate({ taskIds: [taskId], am: tag })
                      setOffen(false)
                    }}
                    className={cn(
                      'tap flex cursor-pointer items-center justify-between rounded-[10px] border px-3 py-3 text-[15px]',
                      aktiv
                        ? 'border-sonne-line bg-sonne-bg font-semibold text-sonne'
                        : 'border-line',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <Sun className="size-4 text-sonne" aria-hidden />{' '}
                      {MARKIER_STUFE_LABELS[stufe]}
                    </span>
                    <span className="text-[12px] text-muted">{tagKurz(tag)}</span>
                  </button>
                )
              })}
              {am && (
                <button
                  type="button"
                  onClick={() => {
                    markieren.mutate({ taskIds: [taskId], am: null })
                    setOffen(false)
                  }}
                  className="tap cursor-pointer rounded-[10px] border border-line px-3 py-3 text-left text-[15px] text-bad"
                >
                  Markierung entfernen
                </button>
              )}
              <button
                type="button"
                onClick={() => setOffen(false)}
                className="tap cursor-pointer rounded-[10px] px-3 py-2.5 text-[14px] text-ink2"
              >
                Abbrechen
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
